// /devices — Camera Tile Grid（spec 16 §4）。
// 縮圖降級：snapshot API 未落地 → 漸層 placeholder + 型號 icon（16 §4.1 定版規則）。
// 新增攝影機：QR 解析 + owner_token 取得可在 Web 完成；BLE/AP 近場 provisioning
// 仍由手機/裝置本地能力接續（16 §7.3 / APP#30 phase 1）。

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DEGRADED_BANNER_TEXT, useConnectionMode } from '../../lib/connection';
import { useTranslation } from 'react-i18next';
import { Illustration } from '../../components/Illustration';
import { formatRelativeUs } from '../../lib/format';
import { isPhoneCam } from '../../lib/phoneCam';
import { productImageUrl } from './productImage';
import { DegradedExplainSheet } from './DegradedExplainSheet';
import type { DeviceSummary } from './deviceModel';
import { PanelPendingBadge } from './PanelCalNotices';
import { batterySummary, capabilityPendingText, recordingModeLabel, cameraClass, deviceIconKind, groupDevicesByClass, deviceName, deviceStateLabel, eventTypeLabel, gradientIndex, isPanelDevice, isSimulated, subscriptionExpired, tileState, type DeviceIconKind } from './deviceModel';
import { quotaBadge, quotaPercent, type QuotaEntry } from './quotaModel';
import { useDeviceList } from './useDeviceList';
import { useDeviceQuota } from './useDeviceQuota';
import { SnoozeControl } from './SnoozeControl';
import { deviceGroupName, devicesInGroup, orderedGroups, MAX_DEVICE_GROUPS } from './groups';
import { migrateRoomsToGroups } from './groupMigration';
import { setDeviceGroup, setDeviceGroupOrder, getDeviceGroupOrder, getEdgeDevices } from '../../lib/api';
import { edgeOnline } from '../../lib/eventAi';
import { loadPins, togglePin, loadSort, saveSort, sortDevices, SORT_MODES, type SortMode, loadDensity, saveDensity, type Density, loadOrder, saveOrder, moveInOrder } from './pinSort';
import { useOptionalAuth } from '../../auth/AuthContext';
import { PairingPanel } from './PairingPanel';
import { PanelJoinPanel } from './PanelJoinPanel';
import { SimIotJoinPanel } from './SimIotJoinPanel';
import { doorbellIdsOf, resolvePanelUrl } from './panels';
import { BatchActionBar } from './BatchActionBar';
import { GroupTabBar, NewGroupDialog, ManageGroupsDialog } from './GroupTabBar';
import { runBatch, type BatchOutcome } from './batchOps';
import './devices.css';

export function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="6" width="14" height="11" rx="2" />
      <path d="m22 8-6 3.5L22 15z" fill="currentColor" stroke="none" />
      <circle cx="9" cy="11.5" r="2.5" />
    </svg>
  );
}

// APP#39：非相機裝置 tile 圖示（插座 / 感測器 / 泛用裝置）。
function PlugIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2v6M15 2v6" />
      <path d="M6 8h12v3a6 6 0 0 1-12 0z" />
      <path d="M12 17v5" />
    </svg>
  );
}
function SensorIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13.5V5a2 2 0 0 1 4 0v8.5a4 4 0 1 1-4 0z" />
      <circle cx="12" cy="17" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
function DeviceIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
// ls-loop 家用面板：用面板 App 的圖示（device/port/ios-panel/design/app-icon.svg；同 Android / iOS 型錄圖 home-panel）
function PanelIcon() {
  return <img className="dv-thumb-product" src="/products/panel.png" alt="" aria-hidden="true" />;
}
function TileIcon({ kind }: { kind: DeviceIconKind }) {
  switch (kind) {
    case 'panel':
      return <PanelIcon />;
    case 'plug':
      return <PlugIcon />;
    case 'sensor':
      return <SensorIcon />;
    case 'device':
      return <DeviceIcon />;
    default:
      return <CameraIcon />;
  }
}

function DeviceTile({
  d,
  pinned,
  onTogglePin,
  quota,
  onExplain,
  reorderMode = false,
  onReorderDrop,
  selectMode = false,
  selected = false,
  onToggleSelect,
}: {
  d: DeviceSummary;
  pinned: boolean;
  onTogglePin: (id: string) => void;
  quota: QuotaEntry | undefined;
  onExplain: (deviceId: string) => void;
  reorderMode?: boolean;
  onReorderDrop?: (fromId: string, toId: string) => void;
  // APP#51 多選批次：selectMode 下點卡片 = 切換勾選（不導頁）。
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const st = tileState(d);
  // W2-02：degraded/offline → 即時觀看入口 disable（spec 10 §4.3）
  const liveDisabled = useConnectionMode() !== 'online';
  // LV 入口 capability 閘（16 §3.2 過濾規則延伸至 Tile ▶，2026-06-12）：
  // non-camera（如 sensor-th 僅 0x8000/0x8010）→ ▶ 整顆不渲染（隱藏優於 disable），
  // 避免點入 start_viewing 30s timeout 白等；unknown = cold-start（21 §2.4）→
  // 不得判為非攝影機，▶ 渲染但 disable +「等待裝置首次連線…」。
  const cc = cameraClass(d);
  const iconKind = deviceIconKind(d); // APP#39：非相機裝置換圖示 + 類別 badge
  const isOwner = d.role === 'owner';
  const expired = isOwner && subscriptionExpired(d);
  // 配額 badge（19 §6.1）：subscription expired 覆蓋條優先，shared device 不顯示 owner-only quota。
  const badge = isOwner && !expired ? quotaBadge(quota) : null;
  const warnPct = badge?.level === 'warning' ? quotaPercent(badge.usageRatio) : null;
  // 卡片圖跟手機同一套規則（2026-10-16 John）：雲端自訂照片 → 產品照 → 線條圖示；不再用最近事件截圖（手機沒有用）
  const cover = d.cover?.url ? resolvePanelUrl(d.cover.url) : null;
  const product = productImageUrl(d.model);
  const recMode = d.recording?.mode;
  // 模式旁邊帶錄到哪裡（雲端 / SD 卡 / 手機）：只看模式分不出錄去哪（615 選雲端被擋回 SD 卡也看不出來）
  const recLoc = d.recording?.location;
  const recWhere = !recMode || recMode === 'off' || recMode === 'none' || !recLoc ? null
    : recLoc === 'cloud' ? t('deviceOverview.recCloud') : isPhoneCam(d.model) ? t('deviceOverview.recPhone') : t('deviceOverview.recSd');
  const recLabel = recMode && recMode !== 'off' && recMode !== 'none' ? recordingModeLabel(recMode) + (recWhere ? ` · ${recWhere}` : '') : null;
  const powerLabel = batterySummary(d);
  const openDetail = () => navigate(`/devices/${encodeURIComponent(d.device_id)}`);
  // selectMode 優先於 reorderMode：點卡片切換勾選、鍵盤 Enter/Space 同步。
  const primaryAction = selectMode ? () => onToggleSelect?.(d.device_id) : reorderMode ? undefined : openDetail;

  return (
    <article
      className={`dv-tile${st === 'offline' ? ' dv-offline' : ''}${reorderMode ? ' dv-reorder' : ''}${selectMode ? ' dv-selectable' : ''}${selected ? ' dv-selected' : ''}`}
      data-testid={`device-tile-${d.device_id}`}
      // ls-loop（MW、WCAG）：卡片裡還有星號 / 暫停 / 播放按鈕 → 卡片本身不再當按鈕（巢狀互動元件）；
      // 鍵盤與讀屏改用下面的名稱按鈕，滑鼠點卡片照舊
      draggable={reorderMode && !selectMode}
      onClick={primaryAction}
      onDragStart={reorderMode && !selectMode ? (e) => e.dataTransfer.setData('text/plain', d.device_id) : undefined}
      onDragOver={reorderMode && !selectMode ? (e) => e.preventDefault() : undefined}
      onDrop={
        reorderMode && !selectMode
          ? (e) => {
              e.preventDefault();
              const from = e.dataTransfer.getData('text/plain');
              if (from && from !== d.device_id) onReorderDrop?.(from, d.device_id);
            }
          : undefined
      }
    >
      {selectMode && (
        <span className="dv-select-check" aria-hidden="true">
          {selected ? '☑' : '☐'}
        </span>
      )}
      <div className={`dv-thumb dv-grad-${gradientIndex(d)}`}>
        {cover ? <img className="dv-thumb-img" src={cover} alt="" aria-hidden="true" />
          : product ? <img className="dv-thumb-product" src={product} alt="" aria-hidden="true" /> : <TileIcon kind={iconKind} />}
        <button
          className={`dv-pin${pinned ? ' dv-pinned' : ''}`}
          aria-label={pinned ? t('devices.unpin') : t('devices.pin')}
          aria-pressed={pinned}
          title={pinned ? t('devices.unpin') : t('devices.pin')}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(d.device_id);
          }}
        >
          <span aria-hidden="true">{pinned ? '★' : '☆'}</span>
        </button>
        {expired && <div className="dv-sub-banner">{t('devices.cloudRecordingDisabled')}</div>}
        {badge?.level === 'over' && (
          <button
            type="button"
            className="dv-sub-banner dv-degraded"
            onClick={(e) => {
              e.stopPropagation();
              onExplain(d.device_id);
            }}
          >
            {t('devices.cloudRecordingDegraded')}
          </button>
        )}
        {badge?.level === 'warning' && (
          <button
            type="button"
            className="dv-quota-badge"
            onClick={(e) => {
              e.stopPropagation();
              onExplain(d.device_id);
            }}
          >
            {warnPct != null ? t('devices.quotaUsagePercent', { percent: warnPct }) : t('devices.quotaUsageHigh')}
          </button>
        )}
        {st !== 'offline' && cc !== 'non-camera' && (
          <button
            className="dv-play"
            aria-label={t('devices.watchLive')}
            disabled={liveDisabled || cc === 'unknown'}
            title={cc === 'unknown' ? capabilityPendingText() : liveDisabled ? DEGRADED_BANNER_TEXT : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (liveDisabled || cc === 'unknown') return;
              navigate(`/devices/${encodeURIComponent(d.device_id)}/live`);
            }}
          >
            <span aria-hidden="true">▶</span>
          </button>
        )}
        {/* 韌體可更新:原本只有色點 + title(色彩單一傳達 + 觸控/SR 讀不到)→ 補 role/aria-label（a11y review） */}
        {d.firmware_upgrade?.needs_upgrade && (
          <span
            className="dv-fw-dot"
            role="img"
            aria-label={d.firmware_upgrade.latest_version ? t('devices.firmwareUpgradableTo', { version: d.firmware_upgrade.latest_version }) : t('devices.firmwareUpgradable')}
            title={d.firmware_upgrade.latest_version ? t('devices.firmwareUpgradableTo', { version: d.firmware_upgrade.latest_version }) : t('devices.firmwareUpgradable')}
          />
        )}
      </div>
      <div className="dv-tile-body">
        <div className="dv-tile-title">
          <button
            type="button"
            className="dv-name dv-name-btn"
            role={selectMode ? 'checkbox' : undefined}
            aria-checked={selectMode ? selected : undefined}
            aria-disabled={primaryAction ? undefined : true}
            onClick={(e) => { e.stopPropagation(); primaryAction?.(); }}
          >
            {deviceName(d)}
          </button>
          {iconKind !== 'camera' && <span className="dv-badge dv-badge-class">{t(`devices.tileBadge_${iconKind}`)}</span>}
          {isSimulated(d) && <span className="dv-badge dv-badge-sim" data-testid="sim-tag">{t('devices.simulated')}</span>}
          {isPanelDevice(d) && <PanelPendingBadge panelId={d.device_id} />}
          {d.role && d.role !== 'owner' && <span className="dv-badge">{t('devices.shared')}</span>}
        </div>
        <div className="dv-tile-meta">
          <span className={`dv-dot dv-dot-${st}`} />
          <span className="text-secondary">
            {deviceStateLabel(d)}
            {st === 'offline' && d.online?.last_seen != null ? t('devices.lastOnlineSuffix', { time: formatRelativeUs(d.online.last_seen) }) : ''}
          </span>
          {recLabel && <span className="dv-badge dv-badge-rec">{recLabel}</span>}
          {iconKind === 'panel' && d.panel?.photo_count != null && (
            <span className="dv-badge">{t('panels.photoCount', { count: d.panel.photo_count })}</span>
          )}
          {powerLabel && <span className="dv-badge dv-badge-power">{powerLabel}</span>}
        </div>
        {d.last_event?.event_ts != null && (
          <p className="dv-tile-event text-secondary">
            {t('devices.eventWithTime', { event: eventTypeLabel(d.last_event.event_type), time: formatRelativeUs(d.last_event.event_ts) })}
          </p>
        )}
        <div className="dv-tile-actions">
          <SnoozeControl deviceId={d.device_id} compact />
        </div>
      </div>
    </article>
  );
}

export default function DevicesPage() {
  const { t } = useTranslation();
  const { devices, loading, error, reload } = useDeviceList();
  // 配額快照（GET /v3/me/quota，owner-only；端點不可用 → 空 Map → 無 badge，19 §6.1）。
  const { quotaByDevice } = useDeviceQuota();
  const [showAdd, setShowAdd] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [panelCode, setPanelCode] = useState<string | null>(null);   // 通用輸入貼了面板加入網址 → 帶進面板加入流程
  const [showAddSim, setShowAddSim] = useState(false);   // IoT 模擬器（docs/iot-sim/03-sd.md D4）
  // 首頁零裝置第一屏的「新增攝影機」帶 state.openAdd 進來 → 就地開新增流程（對齊 Android/iOS 零裝置第一屏）。
  const addFromHome = (useLocation().state as { openAdd?: boolean } | null)?.openAdd === true;
  useEffect(() => { if (addFromHome) setShowAdd(true); }, [addFromHome]);
  // 裝置分組(CAM-24 Stage 3c):分組來源=後端 per-user device.group;顯示順序=後端
  // device_group_order 偏好(跨裝置同步)。舊本地「房間」於首次載入一次性遷移至後端(見下方)。
  const uid = useOptionalAuth()?.user?.user_id ?? '';
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [editGroups, setEditGroups] = useState(false); // 「編輯分組」模式:每張卡顯示分組下拉,寫回後端
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  // 載入分組排序偏好(uid 到位後)。失敗 → 維持空序(仍可由裝置上的 group 推導分頁)。
  useEffect(() => {
    let alive = true;
    getDeviceGroupOrder()
      .then((o) => { if (alive) setGroupOrder(o); })
      .catch(() => {});
    return () => { alive = false; };
  }, [uid]);
  // 已知分組(排序偏好 ∪ 裝置上出現的分組;對齊 Android/iOS orderedGroups)。
  const allGroups = useMemo(() => orderedGroups(devices ?? [], groupOrder), [devices, groupOrder]);
  // 選取的分組若已不存在(被刪/改名)→ 收合回「全部」。
  const effectiveGroup = selectedGroup && allGroups.includes(selectedGroup) ? selectedGroup : null;
  // 釘選 + 排序(APP#27,per-uid 本地偏好,同 rooms 模式)
  const [pins, setPins] = useState(() => loadPins(uid));
  const [sort, setSort] = useState<SortMode>(() => loadSort(uid));
  // 卡片密度 + 手動排序(APP#51,VistaFlow 卡片尺寸 / 拖曳排序對標;per-uid 本地偏好)
  const [density, setDensity] = useState<Density>(() => loadDensity(uid));
  const [order, setOrder] = useState<string[]>(() => loadOrder(uid));
  const [reorderMode, setReorderMode] = useState(false);
  // 多選批次(APP#51 後續子項):selectMode + 勾選集;與 reorder/editRooms 互斥。
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setPins(loadPins(uid));
    setSort(loadSort(uid));
    setDensity(loadDensity(uid));
    setOrder(loadOrder(uid));
  }, [uid]);
  // 一次性遷移:舊本地房間 → 後端分組。保守且冪等(見 groupMigration.ts):每 uid 每 session
  // 至多一次,且僅在「本地有房間且目前沒有任何後端分組」時才跑。uid 未到位不跑(避免清掉 boot
  // 競速鏡像)。成功後重載裝置 + 重讀排序偏好。
  useEffect(() => {
    if (!uid || !devices) return;
    let alive = true;
    void migrateRoomsToGroups(uid, devices).then((migrated) => {
      if (migrated && alive) {
        void reload();
        getDeviceGroupOrder().then((o) => { if (alive) setGroupOrder(o); }).catch(() => {});
      }
    });
    return () => { alive = false; };
  }, [uid, devices, reload]);

  // ---- 分組寫入(全部走後端 setDeviceGroup + device_group_order;寫後重載)----
  // 建立新分組:附加到排序偏好並持久化,隨即選中(建立的是空分組,尚無成員)。
  const createGroup = async (name: string) => {
    const g = name.trim();
    setNewGroupOpen(false);
    if (!g || groupOrder.includes(g)) return;
    const next = [...groupOrder, g];
    setGroupOrder(next);
    setSelectedGroup(g);
    await setDeviceGroupOrder(next);
  };
  // 指派單台裝置到分組(空字串=移出分組);新分組名附加到排序偏好。之後重載。
  const assignDeviceGroup = async (deviceId: string, group: string) => {
    await setDeviceGroup(deviceId, group);
    const name = group.trim();
    if (name && !groupOrder.includes(name)) {
      const next = [...groupOrder, name];
      setGroupOrder(next);
      await setDeviceGroupOrder(next);
    }
    await reload();
  };
  // 改名:批次改寫所有成員 device.group + 排序清單換名(去重)+ 重指選取。
  const renameGroup = async (oldName: string, newName: string) => {
    const nn = newName.trim();
    if (!nn || nn === oldName) return;
    const members = (devices ?? []).filter((d) => deviceGroupName(d) === oldName);
    for (const d of members) await setDeviceGroup(d.device_id, nn);
    const next = Array.from(new Set(groupOrder.map((g) => (g === oldName ? nn : g))));
    setGroupOrder(next);
    if (selectedGroup === oldName) setSelectedGroup(nn);
    await setDeviceGroupOrder(next);
    await reload();
  };
  // 刪除:成員 group 清空(移出分組)+ 從排序清單移除;若正選中則收合回全部。
  const deleteGroup = async (g: string) => {
    const members = (devices ?? []).filter((d) => deviceGroupName(d) === g);
    for (const d of members) await setDeviceGroup(d.device_id, '');
    const next = groupOrder.filter((x) => x !== g);
    setGroupOrder(next);
    if (selectedGroup === g) setSelectedGroup(null);
    await setDeviceGroupOrder(next);
    await reload();
  };
  // 上下重排:把目前完整已知順序(含尚未入偏好的裝置分組)提升為新的排序偏好並持久化。
  const moveGroup = async (index: number, delta: -1 | 1) => {
    const arr = [...allGroups];
    const j = index + delta;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setGroupOrder(arr);
    await setDeviceGroupOrder(arr);
  };
  // 批次移到分組(供 BatchActionBar):逐台 setDeviceGroup,新分組名附加到排序偏好。回傳部分失敗彙整。
  const batchMoveGroup = async (ids: string[], group: string): Promise<BatchOutcome> => {
    const out = await runBatch(ids, (id) => setDeviceGroup(id, group));
    const name = group.trim();
    if (name && !groupOrder.includes(name)) {
      const next = [...groupOrder, name];
      setGroupOrder(next);
      await setDeviceGroupOrder(next);
    }
    return out;
  };

  const onTogglePin = (id: string) => setPins(togglePin(uid, id));
  const toggleDensity = () => setDensity((d) => saveDensity(uid, d === 'compact' ? 'comfortable' : 'compact'));
  // 拖曳落點:把 fromId 移到 toId 之前,存本地並切到 custom 排序讓結果生效。
  const onReorderDrop = (fromId: string, toId: string) => {
    const present = devices ? devices.map((x) => x.device_id) : [];
    const next = moveInOrder(order, fromId, toId, present);
    setOrder(saveOrder(uid, next));
    if (sort !== 'custom') setSort(saveSort(uid, 'custom'));
  };
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // 進多選 → 關掉 reorder/editGroups(互斥);離開 → 清勾選。
  const enterSelectMode = () => {
    setReorderMode(false);
    setEditGroups(false);
    setSelectMode(true);
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };
  const [sheetDeviceId, setSheetDeviceId] = useState<string | null>(null);
  const sheetDevice = sheetDeviceId && devices ? devices.find((d) => d.device_id === sheetDeviceId) ?? null : null;
  // 多機分割入口（APP#145 B2，對齊 iOS `devices_multiview` toolbar／Android 右上宮格）：
  // 兩台以上攝影機在線才出現（單機沒有「同看」可言）；cold-start unknown 不算在線。
  const onlineCameras = devices?.filter((d) => cameraClass(d) === 'camera' && tileState(d) === 'online').length ?? 0;
  const showMultiview = onlineCameras >= 2;

  return (
    <section>
      <header className="dv-page-head">
        <h1>{t('devices.cameras')}</h1>
        {/* 外觀翻新（2026-10-11）：工具列只留「多路同看 / 排序 / 更多」，其餘收進「更多」（手機上原本折成三行）；
            進入多選 / 排序 / 編輯分組時外面多一顆「完成」，不用再打開選單 */}
        <div className="dv-toolbar">
          {showMultiview && (
            <Link className="btn-primary" to="/live/grid" data-testid="multiview-link" aria-label={t('live.gridTitle')}>
              ▦ {t('live.gridTitle')}
            </Link>
          )}
          <label className="dv-sort">
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>{t('devices.sortBy')}</span>
            <select
              value={sort}
              data-testid="devices-sort"
              onChange={(e) => setSort(saveSort(uid, e.target.value as SortMode))}
            >
              {SORT_MODES.map((m) => (
                <option key={m} value={m}>{t(`devices.sort_${m}`)}</option>
              ))}
            </select>
          </label>
          {(selectMode || reorderMode || editGroups) && (
            <button
              className="btn-primary"
              onClick={() => (selectMode ? exitSelectMode() : reorderMode ? setReorderMode(false) : setEditGroups(false))}
            >
              {t('common.done')}
            </button>
          )}
          <details className="dv-more">
            <summary className="btn-ghost">{t('devices.more')}</summary>
            {/* 點了任何一項就收起選單 */}
            <div className="dv-more-menu" onClick={(e) => e.currentTarget.closest('details')?.removeAttribute('open')}>
              {/* ls-loop MV.4：一頁看全部攝影機的在線 / 儲存 / 錄影 / 韌體，有問題的排前面 */}
              <Link to="/vms/devices" data-testid="overview-link">{t('deviceOverview.link')}</Link>
              <button onClick={toggleDensity} data-testid="density-toggle" aria-pressed={density === 'compact'}>
                {density === 'compact' ? t('devices.densityComfortable') : t('devices.densityCompact')}
              </button>
              <button
                onClick={() => (selectMode ? exitSelectMode() : enterSelectMode())}
                data-testid="select-toggle"
                aria-pressed={selectMode}
              >
                {selectMode ? t('common.done') : t('devices.multiSelect')}
              </button>
              <button
                onClick={() => setReorderMode((v) => !v)}
                data-testid="reorder-toggle"
                aria-pressed={reorderMode}
                disabled={selectMode}
              >
                {reorderMode ? t('common.done') : t('devices.reorder')}
              </button>
              <button onClick={() => setEditGroups((v) => !v)} data-testid="group-edit-toggle" disabled={selectMode}>
                {editGroups ? t('common.done') : t('devices.editGroups')}
              </button>
              <button onClick={() => void reload()} disabled={loading}>
                {loading ? t('common.loading') : t('common.refresh')}
              </button>
            </div>
          </details>
        </div>
      </header>

      {reorderMode && (
        <p className="text-secondary" role="status" style={{ margin: '0 0 var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
          {t('devices.reorderHint')}
        </p>
      )}

      {selectMode && devices && (
        <BatchActionBar
          devices={devices}
          selectedIds={selected}
          groupNames={allGroups}
          onMoveGroup={batchMoveGroup}
          onExit={exitSelectMode}
          onClearSelection={() => setSelected(new Set())}
          onReload={reload}
        />
      )}

      {loading && !devices && (
        <div className="dv-grid" aria-label={t('common.loading')}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="dv-tile dv-skeleton">
              <div className="dv-thumb" />
              <div className="dv-tile-body">
                <div className="dv-skel-line" />
                <div className="dv-skel-line short" />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="card dv-state-card">
          <p className="error-text" style={{ margin: 0 }}>
            {t('devices.listLoadFailed', { error })}
          </p>
          <button className="btn-primary" onClick={() => void reload()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {!loading && !error && devices && devices.length === 0 && (
        // 00 §7.2 首屏過渡態：0 台 device 不渲染空 Grid，顯示空狀態 + 新增 CTA
        <div className="card dv-state-card">
          <Illustration name="no-devices" alt="" />
          <h2 style={{ margin: 0 }}>{t('empty.noDevices')}</h2>
          <p className="text-secondary" style={{ margin: 0 }}>
            {t('empty.noDevicesHint')}
          </p>
          <div className="dv-pairing-actions">
            <button className="btn-primary" onClick={() => setShowAdd((v) => !v)}>
              {t('devices.addCamera')}
            </button>
            <button className="btn-ghost" data-testid="add-simiot-empty" onClick={() => setShowAddSim((v) => !v)}>
              {t('simiot.entry')}
            </button>
            <button className="btn-ghost" onClick={() => void reload()}>
              {t('common.refresh')}
            </button>
          </div>
        </div>
      )}

      {!error && devices && devices.length > 0 && (
        <>
          <GroupTabBar
            groups={allGroups}
            selected={effectiveGroup}
            onSelect={setSelectedGroup}
            onAdd={allGroups.length < MAX_DEVICE_GROUPS ? () => setNewGroupOpen(true) : undefined}
            onManage={allGroups.length > 0 ? () => setManageOpen(true) : undefined}
          />
          {/* 純客戶端分組過濾(對齊三端):「全部」→ 全部裝置;選分頁 → 只該分組。未分組裝置只在「全部」。
              APP#39:過濾後再依 device_class 分組(攝影機 / 智能裝置),單一類別不顯示子標題。
              APP#51:排序含自訂 order,分組後每組套卡片密度 + 拖曳排序 props。 */}
          {groupDevicesByClass(sortDevices(devicesInGroup(devices, effectiveGroup), pins, sort, order)).map((grp, _gi, arr) => (
            <div key={grp.kind}>
              {arr.length > 1 && (
                <h3 className="dv-class-title" data-testid={`class-${grp.kind}`}>
                  {grp.kind === 'camera' ? t('devices.classCamera') : t('devices.classSmartIot')}
                </h3>
              )}
              <div className={`dv-grid${density === 'compact' ? ' dv-compact' : ''}`}>
                {grp.devices.map((d) => (
                  <div key={d.device_id} style={{ display: 'contents' }}>
                    <DeviceTile
                      d={d}
                      pinned={pins.includes(d.device_id)}
                      onTogglePin={onTogglePin}
                      quota={quotaByDevice.get(d.device_id)}
                      onExplain={(id) => setSheetDeviceId(id)}
                      reorderMode={reorderMode}
                      onReorderDrop={onReorderDrop}
                      selectMode={selectMode}
                      selected={selected.has(d.device_id)}
                      onToggleSelect={toggleSelect}
                    />
                    {editGroups && (
                      <div className="dv-group-edit" data-testid={`group-edit-${d.device_id}`}>
                        <select
                          aria-label={t('devices.groupMoveTitle', { name: deviceName(d) })}
                          value={deviceGroupName(d) ?? ''}
                          onChange={(e) => void assignDeviceGroup(d.device_id, e.target.value)}
                        >
                          <option value="">{t('devices.groupNone')}</option>
                          {allGroups.map((g) => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className={`dv-grid${density === 'compact' ? ' dv-compact' : ''}`}>
          <button className="dv-tile dv-add" onClick={() => setShowAdd((v) => !v)}>
            <span className="dv-add-plus">＋</span>
            <span>{t('devices.addCamera')}</span>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {t('devices.addCameraHint')}
            </span>
          </button>
          <button className="dv-tile dv-add" data-testid="add-panel-tile" onClick={() => setShowAddPanel((v) => !v)}>
            <span className="dv-add-plus">＋</span>
            <span>{t('panels.addTitle')}</span>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {t('panels.addHint')}
            </span>
          </button>
          <button className="dv-tile dv-add" data-testid="add-simiot-tile" onClick={() => setShowAddSim((v) => !v)}>
            <span className="dv-add-plus">＋</span>
            <span>{t('simiot.entry')}</span>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {t('simiot.entryHint')}
            </span>
          </button>
          </div>
          {/* AI 電腦（edge-ai）：不是攝影機，不混進上面的格子；另列一區看在線狀態（10/9 TE.4 起只在裝置總覽有，John 10/11 要在這頁也看得到） */}
          {getEdgeDevices<DeviceSummary>().length > 0 && (
            <div className="dv-edge" data-testid="devices-edge">
              <h3 className="dv-class-title">{t('deviceOverview.edgeTitle')}</h3>
              <ul className="dv-edge-list">
                {getEdgeDevices<DeviceSummary>().map((e) => {
                  const on = edgeOnline(e);
                  return (
                    <li key={e.device_id} className="dv-edge-row" data-testid={`devices-edge-${e.device_id}`}>
                      <span className={`dv-edge-dot${on ? ' on' : ''}`} aria-hidden="true" />
                      <span className="dv-edge-name">{deviceName(e)}</span>
                      <span className="text-secondary">
                        {on ? t('deviceOverview.online') : t('deviceOverview.offline')}
                        {e.online?.last_seen ? ` · ${t('deviceOverview.lastSeen', { ago: formatRelativeUs(e.online.last_seen) })}` : ''}
                      </span>
                      {e.firmware_version ? <span className="text-secondary dv-edge-ver">{e.firmware_version}</span> : null}
                    </li>
                  );
                })}
              </ul>
              <p className="text-secondary dv-edge-hint">{t('deviceOverview.edgeHint')}</p>
            </div>
          )}
        </>
      )}

      {showAdd && (
        <PairingPanel onClose={() => setShowAdd(false)} onBound={() => void reload()}
          onPanelCode={(raw) => { setShowAdd(false); setPanelCode(raw); setShowAddPanel(true); }} />
      )}

      {showAddPanel && (
        <PanelJoinPanel key={panelCode ?? ''} initialCode={panelCode ?? undefined} doorbellIds={doorbellIdsOf(devices ?? [])} devices={devices ?? []}
          onClose={() => { setShowAddPanel(false); setPanelCode(null); }} onJoined={() => void reload()} />
      )}

      {showAddSim && (
        <SimIotJoinPanel onClose={() => setShowAddSim(false)} onBound={() => void reload()} />
      )}

      {sheetDeviceId && (
        <DegradedExplainSheet deviceId={sheetDeviceId} deviceLabel={sheetDevice ? deviceName(sheetDevice) : sheetDeviceId} onClose={() => setSheetDeviceId(null)} />
      )}

      {newGroupOpen && (
        <NewGroupDialog existing={allGroups} onCreate={(name) => void createGroup(name)} onClose={() => setNewGroupOpen(false)} />
      )}

      {manageOpen && (
        <ManageGroupsDialog
          groups={allGroups}
          onMove={(i, delta) => void moveGroup(i, delta)}
          onRename={(oldName, newName) => void renameGroup(oldName, newName)}
          onDelete={(g) => void deleteGroup(g)}
          onClose={() => setManageOpen(false)}
        />
      )}
    </section>
  );
}
